/**
 * CDB-TQS API — Token Usage, Token Limits, KA Relations and Maintenance
 */

import { CDB_TQS_API_BASE_URL, fetchWithLogging } from "./http";

// ========================================================================
// Token Usage Types
// ========================================================================

export interface TokenUsageSummary {
  total_tokens: number
  total_cost_usd: number
  total_requests: number
  period_days: number
}

export interface TokenUsageByModel {
  model: string
  /** Serving backend: 'openai' | 'azure' | 'litellm_gateway'; null for historical rows. */
  provider: string | null
  total_tokens: number
  cost_usd: number
  request_count: number
  percentage: number
}

export interface TokenUsageByProvider {
  /** 'openai' | 'azure' | 'litellm_gateway' | 'unknown' (NULL historical rows). */
  provider: string
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
  cost_usd: number
  request_count: number
  percentage: number
}

/** Frontend/backend split filter shared by the analytics endpoints. */
export type TokenUsageSource = 'chat' | 'backend'

// ========================================================================
// Flexible query (Power-BI dashboard, cdb_webos#235)
// ========================================================================

/** Group/filter dimensions accepted by POST /token-usage/query. */
export type UsageDimension =
  | 'model' | 'provider' | 'source' | 'client_id' | 'user_id'
  | 'knowledge_area' | 'call_type' | 'day'

export interface UsageQueryRequest {
  /** 0–2 dimensions; [] = KPI totals, 1 = breakdown, 2 = pivot/matrix. */
  group_by?: UsageDimension[]
  /** Equality filters, e.g. { model: 'gpt-5', source: 'chat' }. */
  filters?: Partial<Record<UsageDimension, string>>
  from_date?: string
  to_date?: string
  period_days?: number
}

/** One result row: the requested group keys plus aggregates. */
export type UsageRow = Partial<Record<UsageDimension, string>> & {
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
  cost_usd: number
  request_count: number
}

export interface TokenUsageByCallType {
  call_type: string
  total_tokens: number
  cost_usd: number
  request_count: number
  percentage: number
}

export interface TokenUsageByKA {
  knowledge_area: string
  total_tokens: number
  cost_usd: number
  request_count: number
}

export interface TokenUsageByClient {
  client_id: string
  client_name: string
  total_tokens: number
  cost_usd: number
  request_count: number
  knowledge_areas: string[]
}

export interface TokenUsageDailyPoint {
  date: string
  total_tokens: number
  cost_usd: number
  request_count: number
}

// ========================================================================
// Token Limits Types (Phase 1)
// ========================================================================

export interface TokenLimitSetRequest {
  monthly_token_limit?: number | null
  monthly_cost_limit_usd?: number | null
  warning_threshold_pct?: number
  is_active?: boolean
}

export interface TokenLimitResponse {
  client_id: string
  client_name: string
  monthly_token_limit: number | null
  monthly_cost_limit_usd: number | null
  warning_threshold_pct: number
  is_active: boolean
  current_month_tokens: number
  current_month_cost_usd: number
  current_month_requests: number
  usage_percentage: number
  cost_percentage: number
  is_over_limit: boolean
  is_over_cost_limit: boolean
  is_warning: boolean
  period_start: string
  period_end: string
}

export interface UserTokenUsage {
  user_id: string
  auth_type?: string
  user_email: string | null
  user_name: string | null
  total_tokens: number
  total_cost_usd: number
  total_requests: number
  percentage_of_client: number
}

export interface ByUserResponse {
  client_id: string | null
  client_name: string | null
  period_days: number
  users: UserTokenUsage[]
}

// ========================================================================
// Per-User Token Limits Types (Phase 2)
// ========================================================================

export interface UserLimitSetRequest {
  monthly_token_limit?: number | null
  monthly_cost_limit_usd?: number | null
  is_active?: boolean
}

export interface UserLimitResponse {
  user_id: string
  user_email: string | null
  user_name: string | null
  client_id: string | null
  monthly_token_limit: number | null
  monthly_cost_limit_usd: number | null
  is_active: boolean
  set_by: string | null
  current_month_tokens: number
  current_month_cost_usd: number
  current_month_requests: number
  usage_percentage: number
  cost_percentage: number
  is_over_limit: boolean
  is_over_cost_limit: boolean
  /**
   * A one-off quota grant in effect this month (cdb_webos#675). Deliberately
   * separate from `monthly_token_limit`, which stays the CONFIGURED value the
   * editor writes back — the status flags and percentages above are already
   * computed against limit + grant.
   */
  granted_tokens?: number | null
  granted_cost_usd?: number | null
}

export interface MyUsageResponse {
  user_id: string
  current_month_tokens: number
  current_month_cost_usd: number
  current_month_requests: number
  monthly_token_limit: number | null
  monthly_cost_limit_usd: number | null
  usage_percentage: number
  cost_percentage: number
  is_over_limit: boolean
  is_over_cost_limit: boolean
  period_start: string
  period_end: string
  /**
   * Which limit is blocking. 'user' can be lifted by a supervisor; 'client'
   * needs an admin to raise the client budget, so the two cannot offer the
   * same action (cdb_webos#675). null while not blocked.
   */
  blocked_by?: 'user' | 'client' | null
}

// ========================================================================
// Token Quota Requests (cdb_webos#675)
// ========================================================================

export type QuotaRequestScope = 'user' | 'client'
export type QuotaRequestStatus =
  | 'pending'
  | 'escalated'
  | 'approved'
  | 'declined'
  | 'resolved'

export interface QuotaRequest {
  id: string
  scope: QuotaRequestScope
  requester_id: string
  requester_name: string | null
  requester_email: string | null
  client_id: string
  client_name: string | null
  subject_user_id: string | null
  blocked_by: 'user' | 'client' | null
  dimension: 'tokens' | 'cost' | 'both'
  usage_snapshot: {
    current_month_tokens?: number
    current_month_cost_usd?: number
    current_month_requests?: number
    usage_percentage?: number
    cost_usage_percentage?: number
    monthly_cost_limit_usd?: number | null
    message?: string | null
  } | null
  message: string | null
  status: QuotaRequestStatus
  parent_request_id: string | null
  granted_tokens: number | null
  granted_cost_usd: number | null
  decided_by: string | null
  decided_at: string | null
  decision_note: string | null
  created_at: string
  updated_at: string
  /**
   * Only set when approving a CLIENT request: the requesters whose escalated
   * request this just unblocked. They still need telling, and the server
   * cannot do it — notifications live in the BFF.
   */
  freed_user_ids?: string[]
  /**
   * Only set when declining a CLIENT request: the requesters whose escalated
   * request was closed with it. They asked while the client budget was the
   * blocker, so no per-user grant could have satisfied them either.
   */
  declined_user_ids?: string[]
}

export interface QuotaRequestListResponse {
  items: QuotaRequest[]
  total: number
}

export interface QuotaDecision {
  bonus_tokens?: number | null
  bonus_cost_usd?: number | null
  note?: string | null
}

// ========================================================================
// KA Relations Types
// ========================================================================

export interface KARelationsMatrixKA {
  id: string;
  name: string;
  display_name: string;
  graph_instance_id: string | null;
  instance_name: string | null;
}

export interface KARelatedEntry {
  name: string;
  display_name: string;
  relation_id: string;
  relation_type: string;
  is_bidirectional: boolean;
}

export interface KARelationsMatrixResponse {
  knowledge_areas: KARelationsMatrixKA[];
  relations: Record<string, KARelatedEntry[]>;
}

// List endpoints return { items: [...], total_clients } or a raw array
function unwrapItems<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  const items = (data as { items?: unknown } | null)?.items
  if (Array.isArray(items)) return items as T[]
  return []
}

/**
 * The four client-level token-limit families (chat, backend, API chat,
 * API backend) share identical CRUD shapes and differ only in base path.
 * List routes need the trailing slash to avoid a 307 redirect.
 */
function makeClientLimitApi(basePath: string) {
  return {
    async list(): Promise<TokenLimitResponse[]> {
      const response = await fetchWithLogging(`${basePath}/`, {
        method: 'GET',
      })
      return unwrapItems<TokenLimitResponse>(response.data)
    },

    async get(clientId: string): Promise<TokenLimitResponse> {
      const response = await fetchWithLogging(
        `${basePath}/${clientId}`,
        { method: 'GET' }
      )
      return response.data as TokenLimitResponse
    },

    async set(
      clientId: string,
      config: TokenLimitSetRequest
    ): Promise<TokenLimitResponse> {
      const response = await fetchWithLogging(
        `${basePath}/${clientId}`,
        {
          method: 'PUT',
          body: JSON.stringify(config),
        }
      )
      return response.data as TokenLimitResponse
    },

    async remove(clientId: string): Promise<void> {
      await fetchWithLogging(`${basePath}/${clientId}`, {
        method: 'DELETE',
      })
    },
  }
}

/** Per-user counterpart of makeClientLimitApi (chat + backend families). */
function makeUserLimitApi(basePath: string) {
  return {
    async list(clientId?: string): Promise<UserLimitResponse[]> {
      const params: Record<string, string> = {}
      if (clientId) params.client_id = clientId
      const response = await fetchWithLogging(basePath, {
        method: 'GET',
        params,
      })
      return unwrapItems<UserLimitResponse>(response.data)
    },

    async set(
      userId: string,
      config: UserLimitSetRequest
    ): Promise<UserLimitResponse> {
      const response = await fetchWithLogging(
        `${basePath}/${userId}`,
        {
          method: 'PUT',
          body: JSON.stringify(config),
        }
      )
      return response.data as UserLimitResponse
    },

    async remove(userId: string): Promise<void> {
      await fetchWithLogging(`${basePath}/${userId}`, {
        method: 'DELETE',
      })
    },
  }
}

const tokenLimits = makeClientLimitApi(`${CDB_TQS_API_BASE_URL}/token-limits`)
const backendTokenLimits = makeClientLimitApi(`${CDB_TQS_API_BASE_URL}/token-limits/backend`)
const apiChatLimits = makeClientLimitApi(`${CDB_TQS_API_BASE_URL}/token-limits/api/chat`)
const apiBackendLimits = makeClientLimitApi(`${CDB_TQS_API_BASE_URL}/token-limits/api/backend`)
const userTokenLimits = makeUserLimitApi(`${CDB_TQS_API_BASE_URL}/token-limits/users`)
const backendUserTokenLimits = makeUserLimitApi(`${CDB_TQS_API_BASE_URL}/token-limits/backend/users`)

export const tokenUsageApi = {
  // ========================================================================
  // Flexible query (Power-BI dashboard) — admin = global, supervisor = own client
  // ========================================================================

  async queryUsage(req: UsageQueryRequest): Promise<UsageRow[]> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/query`, {
      method: 'POST',
      body: JSON.stringify({
        group_by: req.group_by ?? [],
        filters: req.filters ?? {},
        from_date: req.from_date,
        to_date: req.to_date,
        period_days: req.period_days ?? 30,
      }),
    })
    return Array.isArray(response.data) ? (response.data as UsageRow[]) : []
  },

  /** Pull the latest Azure retail prices into model_pricing (admin). Returns the sync summary. */
  async refreshPricing(): Promise<{ updated: string[]; skipped: string[]; warnings: string[] }> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/pricing/refresh`, {
      method: 'POST',
    })
    return response.data as { updated: string[]; skipped: string[]; warnings: string[] }
  },

  // ========================================================================
  // Token Usage API (admin-only)
  // ========================================================================

  async getTokenUsageSummary(periodDays: number = 30, clientId?: string, source?: TokenUsageSource): Promise<TokenUsageSummary> {
    const params: Record<string, string> = { period_days: String(periodDays) }
    if (clientId) params.client_id = clientId
    if (source) params.source = source
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/summary`, {
      method: 'GET',
      params,
    })
    return response.data as TokenUsageSummary
  },

  async getTokenUsageByModel(periodDays: number = 30, clientId?: string, source?: TokenUsageSource): Promise<TokenUsageByModel[]> {
    const params: Record<string, string> = { period_days: String(periodDays) }
    if (clientId) params.client_id = clientId
    if (source) params.source = source
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/by-model`, {
      method: 'GET',
      params,
    })
    return response.data as TokenUsageByModel[]
  },

  async getTokenUsageByProvider(periodDays: number = 30, clientId?: string, source?: TokenUsageSource): Promise<TokenUsageByProvider[]> {
    const params: Record<string, string> = { period_days: String(periodDays) }
    if (clientId) params.client_id = clientId
    if (source) params.source = source
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/by-provider`, {
      method: 'GET',
      params,
    })
    return response.data as TokenUsageByProvider[]
  },

  async getTokenUsageByCallType(periodDays: number = 30, clientId?: string, source?: TokenUsageSource): Promise<TokenUsageByCallType[]> {
    const params: Record<string, string> = { period_days: String(periodDays) }
    if (clientId) params.client_id = clientId
    if (source) params.source = source
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/by-call-type`, {
      method: 'GET',
      params,
    })
    return response.data as TokenUsageByCallType[]
  },

  async getTokenUsageByKnowledgeArea(periodDays: number = 30, clientId?: string, source?: TokenUsageSource): Promise<TokenUsageByKA[]> {
    const params: Record<string, string> = { period_days: String(periodDays) }
    if (clientId) params.client_id = clientId
    if (source) params.source = source
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/by-knowledge-area`, {
      method: 'GET',
      params,
    })
    return response.data as TokenUsageByKA[]
  },

  async getTokenUsageByClient(periodDays: number = 30): Promise<TokenUsageByClient[]> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/by-client`, {
      method: 'GET',
      params: { period_days: String(periodDays) },
    })
    return response.data as TokenUsageByClient[]
  },

  async getTokenUsageDaily(periodDays: number = 30, clientId?: string, source?: TokenUsageSource): Promise<TokenUsageDailyPoint[]> {
    const params: Record<string, string> = { period_days: String(periodDays) }
    if (clientId) params.client_id = clientId
    if (source) params.source = source
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/daily`, {
      method: 'GET',
      params,
    })
    return response.data as TokenUsageDailyPoint[]
  },

  // ========================================================================
  // Token Limits Management API
  // (client-level Phase 1, per-user Phase 2, backend Phase 8, API Phase 14)
  // ========================================================================

  getTokenLimits: tokenLimits.list,
  getTokenLimit: tokenLimits.get,
  setTokenLimit: tokenLimits.set,
  deleteTokenLimit: tokenLimits.remove,

  getUserTokenLimits: userTokenLimits.list,
  setUserTokenLimit: userTokenLimits.set,
  deleteUserTokenLimit: userTokenLimits.remove,

  getBackendTokenLimits: backendTokenLimits.list,
  getBackendTokenLimit: backendTokenLimits.get,
  setBackendTokenLimit: backendTokenLimits.set,
  deleteBackendTokenLimit: backendTokenLimits.remove,

  getBackendUserTokenLimits: backendUserTokenLimits.list,
  setBackendUserTokenLimit: backendUserTokenLimits.set,
  deleteBackendUserTokenLimit: backendUserTokenLimits.remove,

  getApiChatLimits: apiChatLimits.list,
  setApiChatLimit: apiChatLimits.set,
  deleteApiChatLimit: apiChatLimits.remove,

  getApiBackendLimits: apiBackendLimits.list,
  setApiBackendLimit: apiBackendLimits.set,
  deleteApiBackendLimit: apiBackendLimits.remove,

  // ========================================================================
  // Call Type Categories (informational)
  // ========================================================================

  async getCallTypeCategories(): Promise<{ chat: string[]; backend: string[] }> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/call-type-categories`, {
      method: 'GET',
    })
    return response.data as { chat: string[]; backend: string[] }
  },

  // ========================================================================
  // My Usage (any authenticated user)
  // ========================================================================

  async getMyUsage(): Promise<MyUsageResponse> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/my-usage`, {
      method: 'GET',
    })
    return response.data as MyUsageResponse
  },

  async getMyBackendUsage(): Promise<MyUsageResponse> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/my-backend-usage`, {
      method: 'GET',
    })
    return response.data as MyUsageResponse
  },

  // ========================================================================
  // Token Quota Requests (cdb_webos#675)
  // ========================================================================

  /** Ask for more quota. The server decides which limit blocks — no amount. */
  async createQuotaRequest(message?: string): Promise<QuotaRequest> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-quota-requests`, {
      method: 'POST',
      body: JSON.stringify({ message: message ?? null }),
    })
    return response.data as QuotaRequest
  },

  /** The caller's own requests — drives the "pending" state in the composer. */
  async getMyQuotaRequests(): Promise<QuotaRequestListResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/token-quota-requests/mine`,
      { method: 'GET' },
    )
    return response.data as QuotaRequestListResponse
  },

  /** The approver queue. Supervisors are pinned to their own client server-side. */
  async getQuotaRequests(openOnly = true): Promise<QuotaRequestListResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/token-quota-requests?open_only=${openOnly}`,
      { method: 'GET' },
    )
    return response.data as QuotaRequestListResponse
  },

  async approveQuotaRequest(id: string, decision: QuotaDecision): Promise<QuotaRequest> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/token-quota-requests/${id}/approve`,
      { method: 'POST', body: JSON.stringify(decision) },
    )
    return response.data as QuotaRequest
  },

  async declineQuotaRequest(id: string, note?: string): Promise<QuotaRequest> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/token-quota-requests/${id}/decline`,
      { method: 'POST', body: JSON.stringify({ note: note ?? null }) },
    )
    return response.data as QuotaRequest
  },

  /** Hand a client-budget block up to the admins. Returns the CLIENT request. */
  async escalateQuotaRequest(id: string, message?: string): Promise<QuotaRequest> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/token-quota-requests/${id}/escalate`,
      { method: 'POST', body: JSON.stringify({ message: message ?? null }) },
    )
    return response.data as QuotaRequest
  },

  async refreshTokenLimitsCache(): Promise<{ invalidated_keys: number; message: string }> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-limits/cache-refresh`, {
      method: 'POST',
    })
    return response.data as { invalidated_keys: number; message: string }
  },

  async getTokenUsageByUser(
    periodDays: number = 30,
    clientId?: string
  ): Promise<ByUserResponse> {
    const params: Record<string, string> = { period_days: String(periodDays) }
    if (clientId) params.client_id = clientId
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/token-usage/by-user`, {
      method: 'GET',
      params,
    })
    const data = response.data as ByUserResponse
    // Ensure users is always an array
    if (!data.users) data.users = []
    return data
  },

  // ========================================================================
  // KA Relations API
  // ========================================================================

  /**
   * Get the KA relations matrix (all KAs with their relations)
   * @returns Matrix of KAs and their relations
   */
  async getKARelationsMatrix(): Promise<KARelationsMatrixResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-areas/relations/matrix`,
      { method: "GET" }
    );
    return response.data as KARelationsMatrixResponse;
  },

  /**
   * Update relations for a knowledge area
   * @param kaId - Knowledge area ID
   * @param relatedKaIds - Array of related KA IDs
   * @param relationType - Relation type (default: "includes")
   */
  async updateKARelations(
    kaId: string,
    relatedKaIds: string[],
    relationType: string = "includes"
  ): Promise<void> {
    await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-areas/${kaId}/relations`,
      {
        method: "PUT",
        body: JSON.stringify({
          related_ka_ids: relatedKaIds,
          relation_type: relationType,
        }),
      }
    );
  },

  // ========================================================================
  // Maintenance / Jobs
  // ========================================================================

  async cleanupDuplicateJobs(): Promise<{ removed: number; kept: number; details: string[] }> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/jobs/cleanup-duplicates`,
      { method: "POST" }
    );
    return response.data;
  },
};
